import { render } from "./router.js";
import { api } from "./api.js";
import { user, admin } from "./auth.js";
import { mountOfflineBanner } from "./components/offlineBanner.js";
import { startUpdateCheck } from "./updateCheck.js";
// Imported for its side effect: captures `beforeinstallprompt` as early as
// possible so the sidebar's "Install app" button can trigger a real install.
import "./pwaInstall.js";
import { API_BASE } from "./config.js";
import { tenant } from "./tenant.js";

// Register the service worker as early as possible so the app shell can be
// served from cache on the next offline load. We DON'T await it - boot
// continues even if registration fails. SW upgrades happen automatically;
// the user's next reload picks them up.
if ("serviceWorker" in navigator) {
  navigator.serviceWorker.register("/sw.js").catch(() => { /* SW optional */ });
}

// Ask the browser to make our storage PERSISTENT so it isn't silently evicted
// under storage pressure or "after a while" - which is what leaves the offline
// mail cache empty. Best-effort: installed PWAs are usually granted it
// automatically; a plain tab may be declined, and that's fine (we still cache,
// it's just evictable). Fire-and-forget so it never delays boot.
// Promise.resolve() wrapper: on some browsers `persist` exists while
// `persisted` doesn't, and `undefined.then(...)` would throw at module scope -
// which kills the whole bundle before boot() ever runs.
if (navigator.storage?.persist) {
  try {
    Promise.resolve(navigator.storage.persisted?.())
      .then((already) => { if (!already) return navigator.storage.persist(); })
      .catch(() => { /* not supported / declined - non-fatal */ });
  } catch { /* never let storage probing break boot */ }
}

// --- *.mcmail.ng tenant gate -------------------------------------------------
// The wildcard serves this app on EVERY label under mcmail.ng, so the app must
// reject labels that don't map to a real tenant. Only *.mcmail.ng hosts are
// gated; webmail.mckodev.com.ng and custom CNAME domains are untouched. A
// definite 403 => "unauthorized"; a network/other error => neutral "can't
// verify" retry (never a false lockout for a real tenant during an outage).
async function mcmailTenantGate() {
  const host = window.location.hostname.toLowerCase();
  if (!host.endsWith(".mcmail.ng")) return true;
  try {
    const res = await fetch(
      `${API_BASE}/v1/tenant/resolve?host=${encodeURIComponent(host)}`,
      { method: "GET", credentials: "omit" },
    );
    if (res.ok) {
      const data = await res.json().catch(() => null);
      if (data && data.authorized) {
        tenant.set(data); // stash owner name for the pre-auth login screen
        return true;
      }
    }
    renderTenantBlocked(host, res.status === 403 ? "unauthorized" : "unavailable");
    return false;
  } catch {
    renderTenantBlocked(host, "unavailable");
    return false;
  }
}

function renderTenantBlocked(host, kind) {
  const esc = (v) => String(v).replace(/[&<>]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;" }[c]));
  const unauth = kind === "unauthorized";
  const title = unauth ? "This address isn't available" : "Can't verify this address";
  const body = unauth
    ? `<b>${esc(host)}</b> isn't a McMail workspace.<br>Check the address, or contact your administrator.`
    : `We couldn't reach McMail to verify <b>${esc(host)}</b>.<br>Check your connection and try again.`;
  const app = document.getElementById("app");
  if (app) {
    app.innerHTML = `
      <div style="min-height:100%;display:flex;align-items:center;justify-content:center;padding:24px;
                  font-family:Inter,Roboto,system-ui,-apple-system,'Segoe UI',sans-serif;color:#202124;">
        <div style="max-width:460px;text-align:center;">
          <img src="/mcmail-mark.png" alt="McMail" style="width:56px;height:56px;object-fit:contain;margin:0 auto 16px;border-radius:14px;" onerror="this.style.display='none'">
          <div style="font-size:18px;font-weight:600;margin-bottom:8px;">${esc(title)}</div>
          <p style="font-size:13px;color:#5f6368;margin:0 0 18px;line-height:1.5;">${body}</p>
          ${unauth ? "" : `<button id="mc-tenant-retry" style="padding:9px 18px;border:0;border-radius:999px;background:#e34234;color:#fff;font-size:13px;font-weight:600;cursor:pointer;">Try again</button>`}
        </div>
      </div>`;
    document.getElementById("mc-tenant-retry")?.addEventListener("click", () => window.location.reload());
  }
  document.body.classList.remove("booting");
}

function dismissPreloader() {
  const preload = document.getElementById("mcmail-preload");
  if (!preload) return;
  preload.classList.add("fading");
  // Reveal the app shell now that we're about to fade the preloader out.
  document.body.classList.remove("booting");
  // Remove from the DOM once the fade transition ends so it never blocks input.
  preload.addEventListener("transitionend", () => preload.remove(), { once: true });
  // Safety net in case the browser eats the transitionend event.
  setTimeout(() => preload.remove(), 800);
}

async function boot() {
  // Reject unknown *.mcmail.ng tenant labels before booting the app.
  if (!(await mcmailTenantGate())) { dismissPreloader(); return; }

  mountOfflineBanner();

  // Strip the cache-bust param left by Force reload so it doesn't linger in
  // the address bar or get copied into shared links.
  if (new URLSearchParams(window.location.search).has("_fresh")) {
    const u = new URL(window.location.href);
    u.searchParams.delete("_fresh");
    window.history.replaceState({}, "", u.pathname + u.search + u.hash);
  }

  const path = window.location.pathname;

  try {
    if (path.startsWith("/control-center") || path.startsWith("/admin")) {
      const me = await api.get("/v1/admin/me").catch(() => null);
      if (me) admin.set(me);
    } else if (!path.startsWith("/register")) {
      // Offline-first boot: rehydrate the persisted session synchronously so a
      // cold launch with no network renders the cached inbox instead of
      // bouncing to /login. Only when there's NO persisted session do we block
      // on /v1/me - otherwise we'd flash the login screen for a genuinely
      // signed-in user whose localStorage was cleared.
      const hadSession = user.hydrate();
      if (hadSession) {
        // Revalidate quietly in the background. On success this refreshes the
        // csrf token + profile; on an expired session api.js auto-redirects to
        // /login; a network error (offline) is swallowed and we keep the cache.
        api.get("/v1/me").then((me) => { if (me) user.set(me); }).catch(() => {});
      } else {
        const me = await api.get("/v1/me").catch(() => null);
        if (me) user.set(me);
      }
    }

    await render();

    // Restore any open compose popups from localStorage. Done AFTER the
    // first render so the body is in the DOM.
    if (user.get()) {
      try {
        const mod = await import("./components/composeManager.js");
        await mod.composeManager.restore();
      } catch { /* compose support unavailable */ }

      // Prompt for a display name if the mailbox has none yet.
      try {
        const { maybePromptForName } = await import("./components/namePrompt.js");
        await maybePromptForName();
      } catch { /* non-fatal */ }
    }
  } catch (err) {
    // FAIL-SAFE: never leave a blank page. dismissPreloader() below reveals
    // #app, so if boot threw before anything rendered the user would see
    // nothing at all. Show what broke plus a way out.
    showBootError(err);
  } finally {
    // Always dismiss the preloader - even if boot threw - so a broken state
    // doesn't leave the user staring at a spinner forever.
    dismissPreloader();
  }
}

// Minimal, dependency-free error screen. Rendered with raw DOM so it still
// works when whatever broke is in the app/render layer.
function showBootError(err) {
  try {
    const app = document.getElementById("app");
    if (!app || app.childNodes.length > 0) return;   // something rendered - leave it
    const msg = (err && (err.message || String(err))) || "Unknown error";
    app.innerHTML = `
      <div style="min-height:100%;display:flex;align-items:center;justify-content:center;padding:24px;
                  font-family:Inter,Roboto,system-ui,-apple-system,'Segoe UI',sans-serif;color:#202124;">
        <div style="max-width:520px;text-align:center;">
          <div style="font-size:17px;font-weight:600;margin-bottom:8px;">McMail couldn't finish loading</div>
          <p style="font-size:13px;color:#5f6368;margin:0 0 16px;">
            Something failed while starting the app. A refresh usually fixes it.
          </p>
          <pre style="text-align:left;font-size:11px;color:#8c2c24;background:#fdeceb;border-radius:8px;
                      padding:10px;overflow:auto;max-height:140px;white-space:pre-wrap;">${
            String(msg).replace(/[&<>]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;" }[c]))
          }</pre>
          <div style="margin-top:16px;display:flex;gap:8px;justify-content:center;">
            <button id="mc-boot-reload" style="padding:9px 16px;border:0;border-radius:999px;background:#e34234;
                    color:#fff;font-size:13px;font-weight:600;cursor:pointer;">Reload</button>
            <button id="mc-boot-reset" style="padding:9px 16px;border:1px solid #dadce0;border-radius:999px;
                    background:#fff;color:#202124;font-size:13px;cursor:pointer;">Reset app data</button>
          </div>
        </div>
      </div>`;
    document.getElementById("mc-boot-reload")?.addEventListener("click", () => window.location.reload());
    // Hard reset: tear down SW + caches + storage, then reload fresh. This is
    // the escape hatch for a wedged service worker / stale cached shell.
    document.getElementById("mc-boot-reset")?.addEventListener("click", async () => {
      try {
        if ("serviceWorker" in navigator) {
          const regs = await navigator.serviceWorker.getRegistrations();
          await Promise.all(regs.map((r) => r.unregister().catch(() => false)));
        }
        if ("caches" in window) {
          const keys = await caches.keys();
          await Promise.all(keys.map((k) => caches.delete(k).catch(() => false)));
        }
        try { localStorage.clear(); sessionStorage.clear(); } catch { /* ignore */ }
      } catch { /* best effort */ }
      window.location.replace(`/?_fresh=${Date.now()}`);
    });
  } catch { /* if even this fails, the preloader dismissal is all we can do */ }
}

boot();
startUpdateCheck();
